import React, { createContext, useContext, useEffect, useState } from 'react';
import { Admin, Session } from '../types';
import { apiClient } from '../lib/api';

interface AuthContextType {
  admin: Admin | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

const normalizeAdmin = (admin: any): Admin => ({
  ...admin,
  permissions: Array.isArray(admin.permissions) ? admin.permissions : [],
  roles: Array.isArray(admin.roles) ? admin.roles : [],
  categories: Array.isArray(admin.categories) ? admin.categories : [],
});

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!admin;

  // Функция для обновления токена
  const refreshTokenIfNeeded = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return;

    try {
      const response = await apiClient.refreshToken(refreshToken);
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
    } catch (error) {
      // Если обновление токена не удалось, не делаем ничего
      // Interceptor в api.ts обработает это при следующем запросе
      console.error('Token refresh failed:', error);
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const response = await apiClient.getMe();
          setAdmin(normalizeAdmin(response.data));
        } catch (error: any) {
          // Если получаем 401 ошибку, очищаем токены
          if (error.response?.status === 401) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            setAdmin(null);
          }
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // Периодическое обновление токена каждые 10 минут
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      refreshTokenIfNeeded();
    }, 10 * 60 * 1000); // 10 минут

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Обновление токена при активности пользователя (каждые 5 минут активности)
  useEffect(() => {
    if (!isAuthenticated) return;

    let activityTimer: NodeJS.Timeout;
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      clearTimeout(activityTimer);
      activityTimer = setTimeout(() => {
        refreshTokenIfNeeded();
      }, 5 * 60 * 1000); // 5 минут после последней активности
    };

    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearTimeout(activityTimer);
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated]);

  const login = async (username: string, password: string) => {
    try {
      const response = await apiClient.login({ username, password });
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      // После логина получить полный профиль
      const me = await apiClient.getMe();
      setAdmin(normalizeAdmin(me.data));
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setAdmin(null);
  };

  const value: AuthContextType = {
    admin,
    isLoading,
    login,
    logout,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 